import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api.service';

export const LICENSE_TIERS = {
  EVALUATION: 'EVALUATION',
  PROFESSIONAL: 'PROFESSIONAL',
  ENTERPRISE: 'ENTERPRISE'
};

const LicenseContext = createContext(null);

export function LicenseProvider({ children }) {
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadLicenseInfo = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getPublic('/license/info');
      
      if (response.success) {
        const licenseData = {
          ...response,
          license_key: response.current_license_key,
          created_at: response.created_at,
          expires_at: response.expires_at,
          type: response.type,
          customer: response.customer,
          features: response.features,
          restrictions: response.restrictions
        };
        setLicenseInfo(licenseData);
      } else {
        setError(response.error || 'Failed to load license information');
        setLicenseInfo(null);
      }
    } catch (err) {
      console.error('Failed to load license info:', err);
      setError(err.message || 'Failed to load license information');
      setLicenseInfo(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLicenseInfo();
  }, []);

  const activateLicense = async (licenseKey) => {
    try {
      setIsLoading(true);
      const response = await apiService.postPublic('/license/activate', {
        license_key: licenseKey
      });
      
      if (response.success) {
        await loadLicenseInfo(); // Reload license info after activation
        return { success: true };
      } else {
        return { 
          success: false, 
          error: response.error || 'License activation failed'
        };
      }
    } catch (err) {
      console.error('License activation error:', err);
      return { 
        success: false, 
        error: err.response?.data?.message || err.message || 'Failed to activate license'
      };
    } finally {
      setIsLoading(false);
    }
  };

  const removeLicense = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.postPublic('/license/deactivate');
      
      if (response.success) {
        setLicenseInfo(null);
        return { success: true };
      } else {
        return {
          success: false,
          error: response.error || 'Failed to remove license'
        };
      }
    } catch (err) {
      console.error('License removal error:', err);
      return {
        success: false,
        error: err.response?.data?.message || err.message || 'Failed to remove license'
      };
    } finally {
      setIsLoading(false);
    }
  };

  const getRemainingDays = () => {
    if (!licenseInfo?.expires_at) return 0;
    const expiryDate = new Date(licenseInfo.expires_at);
    const today = new Date();
    const diffTime = expiryDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const isFeatureEnabled = (featureCode) => {
    if (!licenseInfo?.features) return false;
    return licenseInfo.features[featureCode]?.enabled === true;
  };

  const hasFeatureAccess = (feature) => {
    console.log('Checking feature access:', {
      feature,
      licenseInfo,
      features: licenseInfo?.features,
      hasAccess: licenseInfo?.features?.includes(feature)
    });
    
    if (!licenseInfo) return false;
    return licenseInfo.features.includes(feature);
  };

  return (
    <LicenseContext.Provider value={{
      licenseInfo,
      isLoading,
      error,
      activateLicense,
      removeLicense,
      getRemainingDays,
      isFeatureEnabled,
      reloadLicense: loadLicenseInfo,
      hasFeatureAccess,
      licenseType: licenseInfo?.type || null
    }}>
      {children}
    </LicenseContext.Provider>
  );
}

export function useLicense() {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  return context;
} 